import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, Check, Share2 } from 'lucide-react';
import { get } from '../utils/api';

/**
 * Shared Refer & Earn widgets.
 *
 * ReferralProgressBar — the ONE progress bar used everywhere: a full 0→10-point track with a
 * prize icon at each checkpoint (1 · 3 · 5 · 10), spaced proportionally. The fill caps at the
 * end of the track, but the label keeps counting ("11 / 10 points").
 *
 * ReferralMiniPanel — the compact two-row version for the top of Upcoming Lessons:
 * headline + bar, then the invite link with Copy and WhatsApp. No sub-line, deliberately slim so
 * lesson slots stay above the fold. Renders nothing until loaded and nothing on error — it must
 * never get in the way of the lessons list.
 */

export const PRIZE_EMOJI = { refP1: '🎁', refP3: '📱', refP5: '🎡', refP10: '🎮' };

export const REFERRAL_BAR_MAX = 10;

export function formatPoints(n) {
  if (n === null || n === undefined) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

export function referralShareText(link) {
  return `I've been using Nova Learning for my child's maths and they're loving it! Sign up with my link and you'll get 25% off your first month (free trial included): ${link}`;
}

// Prize wording under the bar checkpoints (confirmed): full names on the big bar,
// shortened ones on the slim Upcoming Lessons bar so labels don't collide on phones.
const PRIZE_BAR_LABELS = {
  refP1: { full: '£50 Amazon voucher', short: '£50 Amazon voucher' },
  refP3: { full: 'Amazon Fire HD 8 tablet', short: 'Amazon Fire tablet' },
  refP5: { full: '£120 family experience', short: '£120 experience' },
  refP10: { full: 'Nintendo Switch 2', short: 'Switch 2' },
};

export function ReferralProgressBar({ points, prizes, size = 'md' }) {
  const sm = size === 'sm';
  const trackH = sm ? 'h-2.5' : 'h-4';
  const iconClasses = sm ? 'h-5 w-5 text-[10px]' : 'h-7 w-7 text-sm';
  const iconTop = sm ? '-top-[5px]' : '-top-1.5';
  const iconHalf = sm ? 10 : 14;
  const fillPct = Math.max(0, Math.min(100, ((points || 0) / REFERRAL_BAR_MAX) * 100));
  const overMax = (points || 0) > REFERRAL_BAR_MAX;
  return (
    <div>
      <div className={`relative w-full rounded-full bg-[#EDE8FA] ${trackH}`}>
        <div
          className={`rounded-full bg-gradient-to-r from-[#8B6CF0] to-[#5C3BD1] transition-all duration-700 ${trackH}`}
          style={{ width: `${fillPct}%` }}
        />
        {(prizes || []).map((p) => {
          const left = (p.points / REFERRAL_BAR_MAX) * 100;
          const reached = (points || 0) >= p.points;
          return (
            <div
              key={p.key}
              className={`absolute flex flex-col items-center ${iconTop}`}
              style={{ left: `calc(${left}% - ${iconHalf}px)` }}
              title={`${p.points} pt${p.points === 1 ? '' : 's'} · ${p.label}`}
            >
              <div
                className={`flex items-center justify-center rounded-full border-2 ${iconClasses} ${
                  reached ? 'border-[#5C3BD1] bg-white shadow' : 'border-[#CFC4F5] bg-[#F7F4FE]'
                }`}
              >
                {PRIZE_EMOJI[p.key] || '⭐'}
              </div>
            </div>
          );
        })}
      </div>
      {/* Checkpoint labels: prize name on top (wrapping to two lines where needed), points
          underneath. The last one is right-aligned so it never overflows the track's edge. On very
          narrow screens the slim bar keeps only the points line (the prize name is hidden below
          360px). */}
      <div className={`relative ${sm ? 'mt-3 h-10' : 'mt-4 h-12'}`}>
        {(prizes || []).map((p) => {
          const last = p.points === REFERRAL_BAR_MAX;
          const reached = (points || 0) >= p.points;
          const label = PRIZE_BAR_LABELS[p.key] || { full: p.label, short: p.label };
          return (
            <div
              key={p.key}
              className={`absolute top-0 ${sm ? 'w-14' : 'w-24'} ${last ? 'text-right' : 'text-center'}`}
              style={last ? { right: 0 } : { left: `${(p.points / REFERRAL_BAR_MAX) * 100}%`, transform: 'translateX(-50%)' }}
            >
              <p
                className={`leading-tight ${sm ? 'hidden text-[9px] min-[360px]:block' : 'text-[11px]'} ${
                  reached ? 'font-semibold text-[#5C3BD1]' : 'text-gray-500'
                }`}
              >
                {sm ? label.short : label.full}
              </p>
              <p className={`leading-tight text-gray-400 ${sm ? 'text-[9px]' : 'text-[10px]'}`}>
                {p.points} pt{p.points === 1 ? '' : 's'}
              </p>
            </div>
          );
        })}
      </div>
      {/* Past the end of the track the count keeps going: 11 / 10 points. */}
      {overMax && (
        <p className={`text-right font-semibold text-[#5C3BD1] ${sm ? 'text-[10px]' : 'mt-1 text-xs'}`}>
          {formatPoints(points)} / {REFERRAL_BAR_MAX} points
        </p>
      )}
    </div>
  );
}

/** Compact invite-link row: link + Copy + WhatsApp. */
export function ReferralShareRow({ link, size = 'sm' }) {
  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* input below stays selectable */
    }
  };
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(referralShareText(link))}`, '_blank', 'noopener');
  };
  const compact = size === 'sm';
  const btn = compact
    ? 'flex items-center justify-center rounded-md border px-2 py-1'
    : 'flex items-center justify-center rounded-md border px-3 py-2';
  return (
    <div className="flex items-center gap-1.5">
      <input
        readOnly
        value={link || ''}
        onFocus={(e) => e.target.select()}
        className={`min-w-0 flex-1 rounded-md border border-gray-300 bg-gray-50 text-gray-700 ${
          compact ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm'
        }`}
      />
      <button type="button" onClick={copyLink} title="Copy link" className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}>
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={shareWhatsApp}
        title="Share on WhatsApp"
        className={`${btn} border-[#25D366] bg-[#25D366] text-white hover:bg-[#1ebe5b]`}
      >
        <Share2 className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Slim two-row Refer & Earn panel for the top of Upcoming Lessons. */
export function ReferralMiniPanel() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    get('/referrals/mine', true)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        /* never block the lessons view */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!data) return null;

  return (
    <div className="mb-4 rounded-xl border border-[#E6DDFB] bg-gradient-to-r from-[#F7F4FE] to-white p-3">
      {/* Row 1: headline + bar */}
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-sm font-semibold text-[#2F1E6B]">{data.headline}</p>
        <Link to="/refer" className="shrink-0 text-xs font-semibold text-[#5C3BD1] hover:underline">
          Refer &amp; Earn →
        </Link>
      </div>
      <div className="mt-2">
        <ReferralProgressBar points={data.points} prizes={data.prizes} size="sm" />
      </div>
      {/* Row 2: invite link + copy + WhatsApp */}
      <div className="mt-3">
        <ReferralShareRow link={data.link} size="sm" />
      </div>
    </div>
  );
}
